/**
 * Phase 5Z.31 — Inline Firmen-Filter (page-level dropdown)
 *
 * Drop-in component for any list-page filter bar. Reads + writes the same
 * FirmContext that powers the top-bar switcher, so changing the firm here
 * also updates the top-bar (and vice-versa). Single source of truth → no
 * inconsistencies.
 *
 * Visibility rules:
 *  - If the user is locked to a single firm (no cross-firm permission) →
 *    component renders NOTHING (no clutter for single-firm users).
 *  - If the platform only has 1 firm in the system → also hidden.
 *  - Otherwise: a compact dropdown labelled "Firma" with "Alle Firmen" +
 *    the list of accessible firms.
 *
 * Usage:
 *   import FirmFilter from "@/components/FirmFilter";
 *   <FirmFilter />                                       // default styled
 *   <FirmFilter compact />                               // narrower variant
 *   <FirmFilter label="Schwesterfirma" />                // custom label
 */
import { useFirms } from "@/contexts/FirmContext";
import { Building2, ChevronDown, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export default function FirmFilter({
  compact = false,
  label = "Firma",
  testid = "firm-filter",
}) {
  const { firms, activeFirmId, setActiveFirmId, canSwitchAll, allowedFirmIds } =
    useFirms();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Hide entirely if user is single-firm or platform has <2 firms
  if (!canSwitchAll && (allowedFirmIds?.length || 0) <= 1) return null;
  if ((firms?.length || 0) <= 1) return null;

  const active = firms.find((f) => f.id === activeFirmId);
  const triggerCls = compact
    ? "inline-flex items-center gap-1.5 px-2 py-1.5 rounded text-xs bg-zinc-900 border border-zinc-800 hover:border-zinc-700"
    : "inline-flex items-center gap-2 px-3 py-2 rounded text-sm bg-zinc-900 border border-zinc-800 hover:border-zinc-700";

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className={triggerCls}
      >
        {active ? (
          <>
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: active.color || "#3b82f6" }}
            />
            <span className="font-medium text-zinc-100 truncate max-w-[140px]">
              {active.name}
            </span>
          </>
        ) : (
          <>
            <Building2
              className={compact ? "w-3 h-3 text-zinc-400" : "w-4 h-4 text-zinc-400"}
            />
            <span className="text-zinc-300">{label}: Alle</span>
          </>
        )}
        <ChevronDown
          className={
            compact ? "w-3 h-3 text-zinc-500" : "w-3.5 h-3.5 text-zinc-500"
          }
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 w-64 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl py-1.5"
          data-testid={`${testid}-menu`}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
            {label}
          </div>
          {canSwitchAll && (
            <button
              onClick={() => {
                setActiveFirmId(null);
                setOpen(false);
              }}
              data-testid={`${testid}-all`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-800 ${
                activeFirmId === null ? "text-zinc-100" : "text-zinc-300"
              }`}
            >
              <Building2 className="w-4 h-4 text-zinc-500" />
              <span className="flex-1 text-left">Alle Firmen</span>
              {activeFirmId === null && (
                <Check className="w-3.5 h-3.5 text-emerald-400" />
              )}
            </button>
          )}
          <div className="border-t border-zinc-800 my-1" />
          {firms.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                setActiveFirmId(f.id);
                setOpen(false);
              }}
              data-testid={`${testid}-item-${f.id}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-800 ${
                activeFirmId === f.id ? "text-zinc-100" : "text-zinc-300"
              }`}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: f.color || "#3b82f6" }}
              />
              <span className="flex-1 text-left truncate">{f.name}</span>
              {f.is_default && (
                <span className="text-[9px] uppercase text-amber-400 font-semibold">
                  std
                </span>
              )}
              {activeFirmId === f.id && (
                <Check className="w-3.5 h-3.5 text-emerald-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
